import React from 'react';
import { 
  Home, Wrench, Settings, RefreshCw, CheckCircle, AlertCircle,
  Anchor, Square, AlignJustify, Wind, ShieldCheck, Building
} from 'lucide-react';

const Services = () => {
  const services = [
    {
      id: 1,
      title: "New Door Installation",
      description: "Professional installation with warranty on parts and labor. Galvanized steel options available.",
      icon: Home,
      link: "/installation"
    },
    {
      id: 2,
      title: "Door Repair",
      description: "Fast, reliable repairs for all makes and models. 24/7 emergency service available.",
      icon: Wrench,
      link: "/repair"
    },
    {
      id: 3,
      title: "Opener Service",
      description: "Installation and repair of garage door openers. Smart and traditional options.",
      icon: Settings,
      link: "/opener-service"
    },
    {
      id: 4,
      title: "Spring Replacement",
      description: "High-cycle 25,000+ springs standard. Safe, efficient replacement by experts.",
      icon: RefreshCw,
      link: "/spring-replacement"
    },
    {
      id: 5,
      title: "Maintenance",
      description: "Regular tune-ups to keep your door operating smoothly and safely.",
      icon: CheckCircle,
      link: "/maintenance"
    },
    {
      id: 6,
      title: "Emergency Service",
      description: "Available 24/7 for urgent repairs. Fast response when you need us most.",
      icon: AlertCircle,
      link: "/emergency"
    },
    {
      id: 7,
      title: "Cable Service",
      description: "Professional cable repair and replacement for safe door operation.",
      icon: Anchor,
      link: "/cables"
    },
    {
      id: 8,
      title: "Panel Replacement",
      description: "Replace damaged panels with galvanized steel options.",
      icon: Square,
      link: "/panels"
    },
    {
      id: 9,
      title: "Track Service",
      description: "Precision track alignment and replacement for smooth operation.",
      icon: AlignJustify,
      link: "/tracks"
    },
    {
      id: 10,
      title: "Weatherstripping",
      description: "Seal your garage from Saskatchewan weather with quality materials.",
      icon: Wind,
      link: "/maintenance"
    },
    {
      id: 11,
      title: "Safety Inspections",
      description: "Comprehensive inspections by background-checked professionals.",
      icon: ShieldCheck,
      link: "/maintenance"
    },
    {
      id: 12,
      title: "Commercial Service",
      description: "Specialized services for businesses and industrial garage doors.",
      icon: Building,
      link: "/services"
    }
  ];

  return (
    <section className="py-20 bg-[#0F0F11]">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-4xl md:text-5xl font-black text-white mb-4">
            Complete Garage Door Services
          </h2>
          <p className="text-xl text-neutral-400 font-medium">
            From installation to emergency repairs, we handle everything with expertise and care
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-12">
          {services.map((service) => {
            const IconComponent = service.icon;
            return (
              <a
                key={service.id}
                href={service.link}
                className="group bg-[#050505] border border-white/10 hover:border-[#D4AF37] p-6 rounded-lg shadow-md hover:shadow-[0_8px_32px_rgba(212,175,55,0.2)] transition-all"
              >
                <div className="flex justify-center mb-4">
                  <div className="bg-[#D4AF37]/10 group-hover:bg-[#D4AF37] p-4 rounded-lg transition-colors">
                    <IconComponent size={32} className="text-[#D4AF37] group-hover:text-[#050505] transition-colors" />
                  </div>
                </div>
                
                <h3 className="text-lg font-black text-white mb-2 text-center group-hover:text-[#D4AF37] transition-colors">
                  {service.title}
                </h3>
                
                <p className="text-neutral-400 text-center text-sm font-medium">
                  {service.description}
                </p>
              </a>
            );
          })}
        </div>

        {/* Warranty Highlight */}
        <div className="bg-gradient-to-r from-[#0F0F11] to-[#16161A] border border-[#D4AF37]/30 rounded-xl p-10 max-w-4xl mx-auto mb-12 shadow-2xl">
          <div className="text-center">
            <ShieldCheck size={48} className="mx-auto mb-4 text-[#D4AF37]" />
            <h3 className="text-3xl font-black mb-4 text-white">All Services Include:</h3>
            <div className="grid md:grid-cols-3 gap-6 text-left">
              <div className="bg-[#050505] border border-white/10 p-4 rounded-lg">
                <p className="font-bold text-lg mb-1 text-[#D4AF37]">✓ Full Warranty</p>
                <p className="text-neutral-400 text-sm">Parts and labor covered</p>
              </div>
              <div className="bg-[#050505] border border-white/10 p-4 rounded-lg">
                <p className="font-bold text-lg mb-1 text-[#D4AF37]">✓ High-Quality Parts</p>
                <p className="text-neutral-400 text-sm">25,000+ cycle springs, galvanized steel</p>
              </div>
              <div className="bg-[#050505] border border-white/10 p-4 rounded-lg">
                <p className="font-bold text-lg mb-1 text-[#D4AF37]">✓ Verified Technicians</p>
                <p className="text-neutral-400 text-sm">Background-checked professionals</p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center">
          <p className="text-xl text-neutral-300 mb-6 font-semibold">
            Ready to get started?
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a 
              href="tel:306-262-5995" 
              className="inline-block bg-[#D4AF37] hover:bg-[#F0C84A] text-[#050505] font-black px-10 py-4 rounded-lg text-xl shadow-lg hover:scale-105 transition-all"
            >
              📞 CALL 306-262-5995
            </a>
            <a 
              href="/quote" 
              className="inline-block bg-white/5 hover:bg-white/10 backdrop-blur-xl border border-white/10 hover:border-[#D4AF37]/50 text-white font-black px-10 py-4 rounded-lg text-xl shadow-lg hover:scale-105 transition-all"
            >
              GET FREE QUOTE
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
